<template>
  <BaseView title="Data Overview">
    <template #content>
      <v-container class="containerView screenBackground">
        <v-row
          class="rowView"
          style="height: 100%"
        >
          <v-col
            class="colView"
            :cols="5"
          >
            <v-row class="rowView">
              <v-col
                class="colView h5"
                style="color: var(--color-text)"
              >
                Sensor Issues - {{ sensorIssuesArrayDate }}
              </v-col>
            </v-row>

            <v-row
              class="rowView"
              style="height: calc(40% - 25px)"
            >
              <v-col
                :cols="6"
                class="colView"
              >
                <CardWChart
                  :key="measData"
                  :info-text="{
                    visible: true,
                    description: 'Displays the overall number of detected sensor faults per day.'
                  }"
                  :card-data="measData"
                />
              </v-col>

              <v-col
                :cols="6"
                class="colView"
              >
                <CardWChart
                  :key="missingData"
                  :card-data="missingData"
                  :info-text="{
                    visible: true,
                    description: 'Displays the percentage of sensor signals received daily relative to the expected number of signals.'
                  }"
                  info-tag="do_completeness"
                />
              </v-col>
            </v-row>

            <v-row
              class="rowView"
              style="height: 60%"
            >
              <v-col
                :cols="12"
                class="colView"
              >
                <v-card>
                  <v-card-title class="bodyBold">
                    Faulty Sensors
                    <InfoIcon
                      :info-data="infoDetails['do_faulty_sensors']"
                    />
                  </v-card-title>

                  <v-card-actions
                    ref="parentDiv"
                    v-resize="updateDataTableHeight"
                  >
                    <v-data-table
                      style="height: 100%; width: 100%"
                      :height="dataTableHeight - 5"
                      single-select
                      disable-pagination
                      hide-default-footer
                      :headers="gridHeaders"
                      :items="gridData"
                      sort-by="error"
                      :sort-desc="true"
                    >
                      <template v-slot:item.error="slotProps">
                        <div
                          v-if="slotProps.item.error"
                          class="circle ma-auto"
                          :style="'border-color: var(--color-error)'"
                        />
                        <div
                          v-else
                          class="circle ma-auto"
                          :style="'border-color: var(--color-success)'"
                        />
                      </template>

                      <template v-slot:item.triggerDate="slotProps">
                        <div v-if="slotProps.item.error">
                          {{ slotProps.item.triggerDate }}
                        </div>
                      </template>

                      <template v-slot:item.cause="slotProps">
                        <div
                          v-html="slotProps.item['cause']"
                        />
                      </template>
                    </v-data-table>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-col>

          <v-col
            class="colView"
            :cols="7"
          >
            <v-row class="rowView">
              <v-col
                class="colView h5"
                style="color: var(--color-text)"
              >
                Raw Data
              </v-col>
            </v-row>

            <v-row
              class="rowView"
              style="height: 120px"
            >
              <v-col
                :cols="12"
                class="colView"
              >
                <v-card>
                  <v-row
                    class="rowView"
                    style="height: 100%"
                  >
                    <v-col
                      class="colView"
                      :cols="11"
                    >
                      <v-row
                        class="rowView"
                        style="height: 100%"
                      >
                        <div class="tableHeader centerAlign mx-1">
                          Signals
                        </div>

                        <ThemedAutocomplete
                          v-model="selTags"
                          :items="signalOptions"
                          solo
                          flat
                          multiple
                          hide-details
                          :suffix="suffixText"
                          background-color="var(--color-surface)"
                          color="var(--color-text)"
                          item-color="var(--color-primary)"
                          item-text="text"
                          item-value="tag"
                          :height="90"
                          style="max-width: calc(100% - 420px); font-size: 15px"
                          class="my-auto mx-2"
                        />

                        <div class="tableHeader centerAlign mx-2">
                          Date
                        </div>

                        <v-menu
                          v-model="dateMenu"
                          :close-on-content-click="false"
                          :close-on-click="false"
                          lazy
                          transition="scale-transition"
                          full-width
                          min-width="600px"
                        >
                          <template v-slot:activator="{ on }">
                            <ThemedTextField
                              v-model="dateText"
                              filled
                              rounded
                              background-color="var(--color-surface)"
                              hide-details
                              append-icon="event"
                              readonly
                              class="mx-2 my-auto rangeInput"
                              color="var(--color-primary)"
                              style="max-width: 290px; font-size: 13px"
                              v-on="on"
                            />
                          </template>

                          <v-col style="background-color: var(--color-surface)">
                            <v-row>
                              <v-col :cols="6">
                                <v-date-picker
                                  v-model="startDate"
                                  :elevation="0"
                                  no-title
                                  scrollable
                                  color="var(--color-primary)"
                                  event-color="var(--color-primary)"
                                  :min="minDate"
                                  :max="endDate"
                                  :events="calcRange()"
                                />
                              </v-col>

                              <v-col :cols="6">
                                <v-date-picker
                                  v-model="endDate"
                                  :elevation="0"
                                  no-title
                                  scrollable
                                  color="var(--color-primary)"
                                  event-color="var(--color-primary)"
                                  :min="startDate"
                                  :max="maxDate"
                                  :events="calcRange()"
                                />
                              </v-col>
                            </v-row>

                            <v-row class="rowView">
                              <v-spacer />
                              <v-btn
                                text
                                color="var(--color-primary)"
                                class="ma-auto"
                                @click="dateMenu = false"
                              >
                                OK
                              </v-btn>
                            </v-row>
                          </v-col>
                        </v-menu>
                      </v-row>
                    </v-col>

                    <v-col
                      class="colView"
                      :cols="1"
                      style="
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      "
                    >
                      <v-btn
                        text
                        color="var(--color-primary)"
                        class="ma-auto"
                        :disabled="signalOptions.length === 0 || selTags.length === 0"
                        @click="startStream"
                      >
                        Fetch
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <v-row
              class="rowView"
              style="height: calc(100% - 145px)"
            >
              <v-col
                :cols="12"
                class="colView"
              >
                <v-card
                  :loading="loading"
                  color="var(--color-primary)"
                  style="max-height: calc(100vh - 265px) !important"
                >
                  <v-col
                    :cols="12"
                    class="colView"
                    style="overflow-y: auto"
                  >
                    <v-row
                      v-for="chart in signalData"
                      :id="chart.tag"
                      :key="chart.tag"
                      style="background-color: var(--color-surface)"
                      class="rowView"
                      @mousemove="updateCoordinates"
                    >
                      <highcharts
                        :ref="chart.tag"
                        :options="chart"
                        style="width: 100%"
                      />
                    </v-row>
                  </v-col>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </BaseView>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import CardWChart from "../../components//CardWChart.vue";

import Highcharts from "highcharts";
import stockInit from "highcharts/modules/stock";
stockInit(Highcharts);

import boost from "highcharts/modules/boost";
boost(Highcharts);

import nodata from "highcharts/modules/no-data-to-display";
import BaseView from "../components/BaseView.vue";
import ThemedTextField from "@/core/ThemedTextField.vue";
import ThemedAutocomplete from "@/core/ThemedAutocomplete.vue";
import InfoIcon from "@/core/InfoIcon.vue";

nodata(Highcharts);

export default {
  name: "DataOverview",

  components: {
    CardWChart,
    BaseView,
    ThemedTextField,
    ThemedAutocomplete,
    InfoIcon,
  },

  data() {
    return {
      selTags: [],
      maxTags: 6,
      signalOptions: [],
      gridHeaders: [
        {
          text: "",
          value: "error",
          class: "tableHeader bodyText",
          sortable: false,
        },
        {
          text: "Sensor",
          value: "sensor",
          class: "tableHeader bodyText",
          sortable: false,
        },
        {
          text: "Triggered",
          value: "triggerDate",
          class: "tableHeader bodyText",
          sortable: false,
        },
        {
          text: "Issue",
          value: "cause",
          class: "tableHeader bodyText",
          sortable: false,
        },
      ],
      signalData: [],
      loading: false,
      dateMenu: false,
      gridData: [],
      measData: {},
      missingData: {},
      socket: null,
      startDate: "",
      endDate: "",
      maxDate: "",
      minDate: "",
      sensorIssuesArrayDate: "",
      dataTableHeight: 100,
      tooltipPoints: [],
    };
  },

  computed: {
    ...mapState("main", ["infoDetails"]),

    ...mapGetters([
      "getVesselMeasurementErrors",
      "getVesselMeasurementErrorsAggr",
      "getVesselMissingRecords",
      "getWSUrl",
      "getVesselsData",
      "getVesselRangeDates",
      "getDataOverviewList",
    ]),

    dateText: function () {
      if (this.startDate !== "" && this.endDate !== "") {
        return "From " + this.startDate + " to " + this.endDate;
      }

      return "";
    },

    suffixText: function () {
      return this.selTags.length + "/" + this.maxTags;
    },
  },

  watch: {
    getVesselsData: {
      handler: function () {
        if (
          this.getVesselsData &&
          this.getVesselsData.length > 0 &&
          this.gridData.length === 0
        ) {
          this.initializeData();
        }
      },
      deep: true,
      immediate: true,
    },

    "$route.params": {
      handler: function (newParams, oldParams) {
        if (
          newParams.vessel &&
          (!oldParams || newParams.vessel !== oldParams.vessel)
        ) {
          this.initializeData();
        }
      },
      deep: true,
    },

    getDataOverviewList: {
      handler: function (newVal) {

        if (!Array.isArray(newVal)) {
          this.signalOptions = [];
          this.selTags = [];
          return;
        }

        this.signalOptions = newVal
          .filter((item) => !item.is_disabled)
          .map((item) => {
            return {
              tag: item.tag,
              text: item.text,
              raw_signal_mapping: item.raw_signal_mapping,
              unit: item.unit || "",
              decimals: Number.isInteger(Number(item.decimals))
                ? Number(item.decimals)
                : 2,
              is_disabled: item.is_disabled,
              is_default: item.is_default,
            };
          });

        const existingValidTags = this.selTags.filter((tag) => {
          return this.signalOptions.some((item) => item.tag === tag);
        });

        if (existingValidTags.length > 0) {
          this.selTags = existingValidTags.slice(0, this.maxTags);
        } else {
          const defaultTags = this.signalOptions
            .filter((item) => item.is_default)
            .slice(0, this.maxTags)
            .map((item) => item.tag);

          this.selTags = defaultTags;
        }

        if (
          this.startDate &&
      this.endDate &&
      this.getVesselsData &&
      this.getVesselsData.length > 0 &&
      this.signalOptions.length > 0 &&
      this.selTags.length > 0
        ) {
          this.startStream();
        }
      },
      deep: true,
      immediate: true,
    },

    getVesselRangeDates: {
      handler: function (newVal) {
        if (!newVal || !newVal.minDate || !newVal.maxDate) return;

        this.minDate = newVal.minDate.substring(0, 10);
        this.maxDate = newVal.maxDate.substring(0, 10);
        this.startDate = newVal.maxDate.substring(0, 10);
        this.endDate = newVal.maxDate.substring(0, 10);

        if (
          this.startDate &&
          this.endDate &&
          this.signalOptions.length > 0 &&
          this.selTags.length > 0
        ) {
          this.startStream();
        }
      },
      deep: true,
      immediate: true,
    },

    getVesselMeasurementErrorsAggr: function () {
      const chartOptions = {
        credits: {
          enabled: false,
        },
        time: {
          useUTC: true,
        },
        chart: {
          zoomType: "xy",
        },
        title: {
          text: undefined,
        },
        tooltip: {
          formatter: function () {
            return (
              new Date(this.x).toISOString().slice(0, 10) +
              " : " +
              this.point.y.toFixed(0)
            );
          },
          shared: true,
        },
        yAxis: {
          allowDecimals: false,
          gridLineWidth: 2,
          min: 0,
          title: {
            text: undefined,
          },
        },
        xAxis: {
          type: "datetime",
          pointInterval: 24 * 3600 * 1000,
          tickInterval: 14 * 24 * 3600 * 1000,
          title: {
            text: undefined,
          },
          labels: {
            format: "{value:%e %b %Y}",
          },
          crosshair: true,
        },
        exporting: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        series: [
          {
            name: "series",
            type: "scatter",
            data: [],
            marker: {
              enabled: true,
              radius: 3,
            },
            animation: false,
            states: {
              hover: {
                enabled: false,
              },
              normal: {
                animation: false,
              },
              inactive: {
                opacity: 1,
              },
            },
          },
        ],
      };

      const aggrMap = this.getAggrMap();

      const keys = Object.keys(aggrMap).sort(
        (a, b) => new Date(a) - new Date(b)
      );

      if (keys.length === 0) {
        this.measData = {
          title: "Detected Issues",
          Value: null,
          chart: chartOptions,
          date: "-",
        };
      } else {
        for (let k = 0; k < keys.length; k++) {
          chartOptions.series[0].data.push([
            new Date(keys[k]).getTime(),
            aggrMap[keys[k]].length,
          ]);
        }

        this.measData = {
          title: "Detected Issues",
          Value: aggrMap[keys[keys.length - 1]].length,
          chart: chartOptions,
        };

        this.buildFaultySensorsTable();
      }
    },

    getVesselMissingRecords: function () {
      const chartOptions = {
        credits: {
          enabled: false,
        },
        time: {
          useUTC: true,
        },
        chart: {
          zoomType: "xy",
        },
        title: {
          text: undefined,
        },
        tooltip: {
          formatter: function () {
            return (
              new Date(this.x).toISOString().slice(0, 10) +
              " : " +
              this.point.y.toFixed(1) +
              " %"
            );
          },
          shared: true,
        },
        yAxis: {
          allowDecimals: false,
          gridLineWidth: 2,
          min: 0,
          max: 100,
          title: {
            text: undefined,
          },
        },
        xAxis: {
          type: "datetime",
          title: {
            text: undefined,
          },
          pointInterval: 24 * 3600 * 1000,
          tickInterval: 14 * 24 * 3600 * 1000,
          labels: {
            format: "{value:%e %b %Y}",
          },
          crosshair: true,
        },
        exporting: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        series: [
          {
            name: "series",
            type: "scatter",
            data: [],
            marker: {
              enabled: true,
              radius: 3,
            },
            animation: false,
            states: {
              hover: {
                enabled: false,
              },
              normal: {
                animation: false,
              },
              inactive: {
                opacity: 1,
              },
            },
          },
        ],
      };

      const keys = Object.keys(this.getVesselMissingRecords);

      if (keys.length === 0) {
        chartOptions.yAxis.min = undefined;
        chartOptions.yAxis.max = undefined;

        this.missingData = {
          title: "Completeness",
          Value: null,
          chart: chartOptions,
          date: "-",
        };
      } else {
        for (let k = 0; k < keys.length; k++) {
          if (Number(this.getVesselMissingRecords[keys[k]]) >= 0) {
            chartOptions.series[0].data.push([
              new Date(keys[k]).getTime(),
              100 - this.getVesselMissingRecords[keys[k]],
            ]);
          }
        }

        this.missingData = {
          title: "Completeness",
          Value:
            (100 - this.getVesselMissingRecords[keys[keys.length - 1]]).toFixed(
              0
            ) + " %",
          chart: chartOptions,
        };

        if (Number(this.getVesselMissingRecords[keys[keys.length - 1]]) < 0) {
          this.missingData.Value = null;
        }
      }
    },

    getVesselMeasurementErrors: function () {
      this.buildFaultySensorsTable();
    },

    selTags: {
      handler: function () {
        if (this.selTags.length === this.maxTags) {
          this.signalOptions.forEach((item) => {
            item.disabled = !this.selTags.includes(item.tag);
          });
        } else {
          this.signalOptions.forEach((item) => {
            item.disabled = false;
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (
        this.getVesselsData &&
        this.getVesselsData.length > 0 &&
        this.$route.params.vessel
      ) {
        this.initializeData();
      }
    });
  },

  beforeDestroy() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  },

  methods: {
    ...mapActions([
      "fetchVesselMeasurementErrors",
      "fetchVesselMeasurementErrorsAggr",
      "fetchVesselMissingRecords",
      "fetchVesselRangeDates",
      "fetchDataOverviewList",
    ]),

    buildFaultySensorsTable() {
      this.gridData = [];
      this.sensorIssuesArrayDate = "";

      if (
        !this.getVesselMeasurementErrors ||
        !this.getVesselMeasurementErrors.dictionary
      ) {
        return;
      }

      const dictionary = this.getVesselMeasurementErrors.dictionary;
      const keys = Object.keys(dictionary);

      if (this.getVesselMeasurementErrors.date) {
        this.sensorIssuesArrayDate =
          this.getVesselMeasurementErrors.date.substring(0, 10);
      }

      const aggrMap = this.getAggrMap();
      const sensorDates = {};
      const triggerMap = {};

      for (const dateStr in aggrMap) {
        if (!Object.prototype.hasOwnProperty.call(aggrMap, dateStr)) continue;

        const parsed = Date.parse(dateStr);
        if (isNaN(parsed)) continue;

        const iso = new Date(parsed).toISOString().slice(0, 10);
        const sensors = aggrMap[dateStr] || [];

        for (let i = 0; i < sensors.length; i++) {
          const sensorName = sensors[i];

          if (!sensorDates[sensorName]) {
            sensorDates[sensorName] = [];
          }

          sensorDates[sensorName].push(iso);
        }
      }

      for (const sensor in sensorDates) {
        if (!Object.prototype.hasOwnProperty.call(sensorDates, sensor)) continue;

        const dates = sensorDates[sensor].sort(function (a, b) {
          return new Date(a) - new Date(b);
        });

        let streakStart = dates[dates.length - 1];

        for (let j = dates.length - 1; j > 0; j--) {
          const current = new Date(dates[j]);
          const previous = new Date(dates[j - 1]);
          const diffDays = (current - previous) / (1000 * 60 * 60 * 24);

          if (diffDays === 1) {
            streakStart = dates[j - 1];
          } else {
            break;
          }
        }

        triggerMap[sensor] = streakStart;
      }

      for (let k = 0; k < keys.length; k++) {
        const sensorName2 = keys[k];
        const issues = dictionary[sensorName2] || [];
        const hasError = issues.length > 0;

        let aggregate = "";

        if (hasError) {
          aggregate = issues.reduce(function (a, b) {
            return a + "<br/>" + b;
          });
        }

        this.gridData.push({
          error: hasError,
          sensor: sensorName2,
          triggerDate: hasError ? triggerMap[sensorName2] || "" : "",
          cause: aggregate,
        });
      }
    },

    updateDataTableHeight() {
      if (this.$refs["parentDiv"]) {
        this.dataTableHeight = this.$refs["parentDiv"].clientHeight;
      }
    },

    initializeData() {
      const vesselName = this.$route.params.vessel;

      if (!vesselName) {
        return;
      }
      this.fetchDataOverviewList();

      this.fetchVesselMeasurementErrors(vesselName);

      this.fetchVesselMeasurementErrorsAggr({
        name: vesselName,
        days: 30,
      });

      this.fetchVesselMissingRecords({
        name: vesselName,
        days: 30,
      });

      this.fetchVesselRangeDates(vesselName);
    },

    startStream() {

      if (
        !this.getVesselsData ||
    !this.getVesselsData.length ||
    !this.$route.params.vessel ||
    !this.startDate ||
    !this.endDate ||
    !this.signalOptions.length
      ) {
        return;
      }

      const vessel = this.getVesselsData.find(
        (item) => item.Vessel === this.$route.params.vessel
      );

      if (!vessel || !vessel.IMO) return;

      const IMO = vessel.IMO;

      const selectedSignals = this.selTags
        .map((tag) => this.signalOptions.find((item) => item.tag === tag))
        .filter((item) => {
          return (
            item &&
      !item.is_disabled &&
      item.raw_signal_mapping
          );
        });

      const tags = selectedSignals.map((item) => item.raw_signal_mapping);

      const signalByRawTag = selectedSignals.reduce((acc, item) => {
        acc[item.raw_signal_mapping] = item;
        return acc;
      }, {});

      if (!tags.length) return;

      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }

      this.socket = new WebSocket(
        this.getWSUrl +
          "/ws/data/vessel/" +
          IMO +
          "/from/" +
          this.startDate +
          "/to/" +
          this.endDate
      );

      this.loading = true;
      this.signalData = [];

      this.socket.onopen = () => {
        this.socket.send(
          JSON.stringify({
            tags: tags,
          })
        );
      };

      this.socket.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if ("finished" in data) {
          const chartsWithData = this.signalData.filter((chart) => {
            return chart.series[0].data && chart.series[0].data.length > 0;
          });

          if (chartsWithData.length > 0) {
            const minX = Math.min(
              ...chartsWithData.map((chart) => chart.series[0].data[0][0])
            );

            const maxX = Math.max(
              ...chartsWithData.map((chart) => {
                return chart.series[0].data[chart.series[0].data.length - 1][0];
              })
            );

            this.signalData.forEach((item) => {
              item.xAxis.min = minX;
              item.xAxis.max = maxX;
            });
          }

          this.socket.close();
          this.socket = null;
          this.loading = false;
        } else if (this.signalData.length === 0) {
          tags.forEach((tag) => {
            const sigMap = signalByRawTag[tag];

            if (!sigMap) return;

            const ser = {
              name: sigMap.text,
              type: "line",
              data: [],
              marker: {
                enabled: false,
              },
              animation: false,
              states: {
                hover: {
                  enabled: false,
                },
                normal: {
                  animation: false,
                },
                inactive: {
                  opacity: 1,
                },
              },
            };

            if (tag in data) {
              const entries = Object.entries(data[tag])
                .map((item) => [new Date(item[0]).getTime(), item[1]])
                .sort((a, b) => a[0] - b[0]);

              ser.data = entries;
            }

            this.signalData.push({
              tag: tag,
              credits: {
                enabled: false,
              },
              time: {
                useUTC: true,
              },
              chart: {
                zoomType: "xy",
                height: 200,
              },
              tooltip: {
                shared: true,
                formatter: this.tooltipFormatter,
                valueSuffix: sigMap.unit,
                valueDecimals: sigMap.decimals,
                hideDelay: 100,
                shape: "rect",
              },
              title: {
                text: sigMap.text,
                align: "right",
                x: -10,
                style: {
                  color: "var(--color-text)",
                },
              },
              yAxis: {
                gridLineWidth: 2,
                title: {
                  text: "[" + sigMap.unit + "]",
                  align: "high",
                },
              },
              xAxis: {
                showLastLabel: false,
                type: "datetime",
                title: {
                  text: undefined,
                },
                labels: {
                  format: "{value:%H:%M<br>%Y-%m-%d}",
                  step: 2,
                },
                events: {
                  afterSetExtremes: this.afterSetExtremes,
                },
                crosshair: {
                  enabled: true,
                  width: 1,
                  color: "#595959",
                },
              },
              legend: {
                enabled: false,
              },
              series: [ser],
            });
          });
        } else {
          tags.forEach((tag) => {
            if (tag in data) {
              const chart = this.signalData.find((item) => item.tag === tag);

              if (!chart || !chart.series || !chart.series[0]) return;

              const newEntries = Object.entries(data[tag]).map((item) => [
                new Date(item[0]).getTime(),
                item[1],
              ]);

              const ser = chart.series[0];
              const oldEntries = ser.data;

              ser.data = oldEntries
                .concat(newEntries)
                .sort((a, b) => a[0] - b[0]);
            }
          });
        }
      };

      this.socket.onerror = () => {
        this.loading = false;
      };

      this.socket.onclose = () => {
        this.loading = false;
      };
    },

    afterSetExtremes(e) {
      this.signalData.forEach((item) => {
        if (!this.$refs[item.tag] || !this.$refs[item.tag][0]) return;

        const chart = this.$refs[item.tag][0].chart;
        const extremes = chart.xAxis[0].getExtremes();

        if (e.min !== extremes.min && e.max !== extremes.max) {
          chart.xAxis[0].setExtremes(e.min, e.max);
        }
      });
    },

    updateCoordinates(e) {
      this.tooltipPoints = [];

      this.signalData.forEach((item) => {
        if (!this.$refs[item.tag] || !this.$refs[item.tag][0]) return;

        const chart = this.$refs[item.tag][0].chart;
        const ev = chart.pointer.normalize(e);
        const point = chart.series[0].searchPoint(ev, true);

        if (!point) return;

        chart.xAxis[0].drawCrosshair(e, point);
        this.tooltipPoints.push(point);
      });
    },

    tooltipFormatter() {
      if (!this.tooltipPoints || this.tooltipPoints.length === 0) {
        return "";
      }

      let a = "";
      let d = new Date(this.tooltipPoints[0].x).toISOString().replace("T", " ");
      d = d.substring(0, d.length - 8);
      a += "<br>  " + d + "<br>";

      this.tooltipPoints.forEach((element) => {
        const unit =
          " [" + element.series.chart.tooltip.options.valueSuffix + "]";
        const decimals = element.series.chart.tooltip.options.valueDecimals;

        a +=
          "<br> <b>" +
          element.series.name +
          " </b>: " +
          element.y.toFixed(decimals) +
          unit +
          "<br>";
      });

      return a;
    },

    calcRange() {
      const selArray = [];

      const currentDate = new Date(this.startDate);

      while (currentDate < new Date(this.endDate)) {
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        selArray.push(new Date(currentDate).toISOString().substring(0, 10));
      }

      selArray.pop();

      return selArray;
    },

    getAggrMap() {
      if (!this.getVesselMeasurementErrorsAggr) return {};

      if (
        this.getVesselMeasurementErrorsAggr.success &&
        this.getVesselMeasurementErrorsAggr.data
      ) {
        return this.getVesselMeasurementErrorsAggr.data;
      }

      return this.getVesselMeasurementErrorsAggr;
    },
  },
};
</script>

<style scoped>
/* Theme-aware data table styling */
.v-data-table {
  background-color: var(--color-bg) !important;
  color: var(--color-text) !important;
}

.v-data-table >>> th {
  background-color: var(--color-primary) !important;
  color: var(--color-text) !important;
  border-bottom: 1px solid var(--color-border) !important;
}

.v-data-table >>> td {
  border-bottom: 1px solid var(--color-border) !important;
  color: var(--color-text) !important;
}

.v-data-table >>> tbody tr:hover {
  background-color: var(--color-overlay) !important;
}

.v-data-table >>> tbody tr:nth-of-type(odd) {
  background-color: var(--color-surface) !important;
}

.v-data-table >>> tbody tr:nth-of-type(even) {
  background-color: var(--color-bg) !important;
}

.v-card {
  background-color: var(--color-surface) !important;
  color: var(--color-text) !important;
}

.v-btn {
  background-color: var(--color-primary) !important;
  color: var(--color-text) !important;
  border-color: var(--color-border) !important;
}

.v-btn:hover {
  background-color: var(--color-primary-light) !important;
}

.v-btn--disabled {
  background-color: var(--color-neutral) !important;
  color: var(--color-text-muted) !important;
}

.v-btn--plain {
  background-color: transparent !important;
  color: var(--color-text) !important;
}

.v-btn--plain:hover {
  background-color: var(--color-overlay) !important;
}

.v-autocomplete,
.v-text-field {
  background-color: var(--color-surface) !important;
  color: var(--color-text) !important;
}

.v-autocomplete .v-input__slot,
.v-text-field .v-input__slot {
  background-color: var(--color-surface) !important;
  border-color: var(--color-border) !important;
}

.v-autocomplete input,
.v-text-field input {
  color: var(--color-text) !important;
}
</style>
